<script setup>
import { ref, watch, onMounted } from 'vue'
import { mediaUrl } from '@core/utils/mediaUrl'
import axios from '@axios'
import CmsText from '@/@core/components/CmsText.vue'

// Shared Members overview content: How RM Works / FAQs tabs + testimonials.
// Rendered inside MembersInfoDialog and inline on the home page.
const props = defineProps({
  initialTab: {
    type: String,
    default: 'how-rm-works'
  }
})

const tab = ref(props.initialTab)

watch(() => props.initialTab, val => {
  tab.value = val
})

const testimonialsLoading = ref(true)

// Testimonials data
const testimonials = ref([
  {
    profileId: 'justinishbia',
    title: 'Managing Partner - Shore Capital Partners',
    subtitle: 'Alternate Governor - Phoenix Suns',
    quote: 'RM is awesome - everyone on my team across the country is a member & loves it. We use for business dinners & personally. Makes it easy for my wife & I to get last minute date nights at our favorite places & she says so fun!',
    profileData: null
  },
  {
    profileId: 'marinasquerciati',
    title: 'Actor - NBC Universal',
    subtitle: null,
    quote: 'I use RM for last-minute reservations in Chicago and all over the world. I love how convenient it is. I recommended RM to my co-stars and they\'re members too!',
    profileData: null
  },
  {
    profileId: 'tonydavis',
    title: 'Co-Founder and President - Linden Capital Partners',
    subtitle: null,
    quote: 'RM is one of the best employee benefits we\'ve provided. Having restaurateurs friends & investing in restaurants, I already enjoyed great access - now with RM, my team does, too. I also don\'t have to text my friends anymore!',
    profileData: null
  }
])

// Fetch profile data for testimonials
const fetchTestimonialProfiles = async () => {
  testimonialsLoading.value = true
  for (const testimonial of testimonials.value) {
    try {
      const response = await axios.get(`/user/?profileId=${testimonial.profileId}`)
      if (response.data && response.data.length > 0) {
        testimonial.profileData = response.data[0]
      }
    } catch (error) {
      console.error(`Error fetching profile for ${testimonial.profileId}:`, error)
    }
  }
  testimonialsLoading.value = false
}

// Fetch profiles on mount
onMounted(() => {
  fetchTestimonialProfiles()
})

// Get profile image URL
const getProfileImageUrl = profileImageId => {
  return mediaUrl(profileImageId, 'small')
}

// Navigate to profile
const goToProfile = profileId => {
  window.open(`https://reserverm.com/m/${profileId}`, '_blank')
}
</script>

<template>
  <div>
    <!--
      Plain buttons instead of VTabs: Vuetify's slide-group mis-measures
      when mounted inside the home page's expand transition (clipped or
      mispositioned FAQs tab); these are deterministic at any width 
    -->
    <div class="px-4">
      <div class="simple-tabs">
        <button
          class="simple-tab"
          :class="{ 'simple-tab--active': tab === 'how-rm-works' }"
          type="button"
          @click="tab = 'how-rm-works'"
        >
          <CmsText k="members.tabs.howRmWorksTitle" />
        </button>
        <button
          class="simple-tab"
          :class="{ 'simple-tab--active': tab === 'faqs' }"
          type="button"
          @click="tab = 'faqs'"
        >
          <CmsText k="members.tabs.faqsTitle" />
        </button>
      </div>
    </div>

    <VDivider class="mx-4" />

    <div class="pa-4">
      <VWindow v-model="tab">
        <VWindowItem
          class="pt-4 text-xs"
          value="how-rm-works"
        >
          <div class="tab-content-width">
            <CmsText
              k="members.tabs.howRmWorks"
              tag="div"
              class="section-subtitle text-left mt-2 how-rm-works-content"
              style="line-height: 1.4em;"
            />
          </div>
        </VWindowItem>

        <VWindowItem
          class="pt-4 text-xs"
          value="faqs"
        >
          <div class="tab-content-width">
            <CmsText
              k="members.tabs.faqs"
              tag="div"
              class="section-subtitle text-left mt-2 faqs-content"
              style="line-height: 1.4em;"
            />
          </div>
        </VWindowItem>
      </VWindow>

      <VDivider class="mt-6 mb-6" />

      <div class="testimonials-section mb-2">
        <h3
          class="text-sm text-center mb-4"
          style="font-weight: 600; color: rgba(0, 0, 0, 0.6);"
        >
          What members say:
        </h3>

        <div
          v-if="testimonialsLoading"
          class="d-flex justify-center align-center py-6"
        >
          <VProgressCircular
            indeterminate
            color="primary"
            size="32"
          />
        </div>

        <div
          v-for="(testimonial, index) in testimonials"
          v-else
          :key="testimonial.profileId"
          :class="{ 'mt-4': index > 0 }"
        >
          <div class="testimonial-item pa-4">
            <div class="d-flex align-start gap-3 mb-3">
              <div
                class="profile-photo-wrapper"
                style="cursor: pointer;"
                @click="goToProfile(testimonial.profileId)"
              >
                <VAvatar
                  v-if="testimonial.profileData?.profileImageId"
                  size="60"
                  class="profile-avatar"
                >
                  <VImg
                    :src="getProfileImageUrl(testimonial.profileData.profileImageId)"
                    :alt="testimonial.profileData.fullName"
                    cover
                  />
                </VAvatar>
                <VAvatar
                  v-else
                  size="60"
                  color="grey-lighten-2"
                >
                  <VIcon>mdi-account</VIcon>
                </VAvatar>
              </div>
              <div
                class="flex-grow-1"
                style="margin-top: -15px;"
              >
                <div
                  class="testimonial-name"
                  style="cursor: pointer; font-weight: 600; font-size: 0.875rem; line-height: 1.2; color: rgba(0, 0, 0, 0.6);"
                  @click="goToProfile(testimonial.profileId)"
                >
                  {{ testimonial.profileData?.fullName || 'Member' }}
                </div>
                <div
                  class="testimonial-title text-xs"
                  style="color: rgba(0, 0, 0, 0.6); line-height: 1.3; font-weight: 600;"
                >
                  {{ testimonial.title }}<span v-if="testimonial.subtitle"><br>{{ testimonial.subtitle }}</span>
                </div>
              </div>
            </div>
            <div
              class="testimonial-quote text-xs"
              style="line-height: 1.4; font-style: italic;"
            >
              "{{ testimonial.quote }}"
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tab-content-width {
  font-size: 0.8em;
}

.testimonials-section {
  padding: 0;
}

.testimonial-item {
  display: block;
}

.profile-photo-wrapper {
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.profile-photo-wrapper:hover {
  transform: scale(1.05);
}

.profile-avatar {
  border: 2px solid var(--ds-border-default);
}

.testimonial-name:hover {
  text-decoration: underline;
}

.testimonial-title {
  margin-top: 2px;
}

.testimonial-quote {
  color: rgba(0, 0, 0, 0.6);
}

.simple-tabs {
  display: flex;
  width: 100%;
}

.simple-tab {
  flex: 1;
  padding: 10px 8px 8px;
  text-align: center;
  font-family: var(--ds-font-body);
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: var(--ds-text-secondary);
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
}

.simple-tab--active {
  color: rgb(var(--v-theme-primary));
  border-bottom-color: rgb(var(--v-theme-primary));
  font-weight: 600;
}

.how-rm-works-content {
  font-family: inherit !important;
  color: var(--ds-text-primary) !important;
}

.how-rm-works-content :deep(p) {
  margin: 0 0 0.6rem !important;
  font-family: inherit !important;
  font-size: 0.75rem !important;
  color: var(--ds-text-primary) !important;
}

// Overview hierarchy — one font size throughout (0.75rem, matching FAQs);
// bold + spacing carry the structure: bold lead line, mission line grouped
// under it, gap, bold lead-in label, then the list.
.how-rm-works-content :deep(p:nth-of-type(1)) {
  font-weight: 700;
  margin-bottom: 0.75rem !important;
}

.how-rm-works-content :deep(p:nth-of-type(2)) {
  margin-bottom: 0 !important;
}

.how-rm-works-content :deep(p:nth-of-type(3)) {
  font-weight: 700;
  margin: 1rem 0 0.4rem !important;
}

.how-rm-works-content :deep(b) {
  font-family: inherit !important;
  color: rgba(0, 0, 0, 0.6) !important;
}

.how-rm-works-content :deep(a) {
  color: rgb(var(--v-theme-primary));
}

.how-rm-works-content :deep(p.section-title) {
  margin-top: 1rem !important;
}

.how-rm-works-content :deep(p.section-title:first-child) {
  margin-top: 0 !important;
}

// Benefits: a real bulleted list under the lead-in label
.how-rm-works-content :deep(ul) {
  margin: 0;
  padding-left: 1.1rem;
  list-style: disc;
}

.how-rm-works-content :deep(li) {
  font-size: 0.75rem;
  color: var(--ds-text-primary);
  line-height: 1.45;
  margin-bottom: 0.6rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.faqs-content {
  font-family: inherit !important;
  color: rgba(0, 0, 0, 0.6) !important;
}

.faqs-content :deep(p) {
  margin: 0 !important;
  font-family: inherit !important;
  font-size: 0.75rem !important;
  color: rgba(0, 0, 0, 0.6) !important;
}

.faqs-content :deep(b) {
  font-family: inherit !important;
  color: rgba(0, 0, 0, 0.6) !important;
}

.faqs-content :deep(a) {
  color: rgb(var(--v-theme-primary));
}

.faqs-content :deep(p.faq-title) {
  margin-top: 1rem !important;
}

.faqs-content :deep(p.faq-title:first-child) {
  margin-top: 0 !important;
}

.faqs-content :deep(p.faq-subsection) {
  margin-top: 0.5rem !important;
}
</style>
